/**
 * Tenant identity — the pure parts, shared by client and Cloud Functions.
 *
 * The alias map and the "does this doc belong to me?" rule MUST be one
 * implementation. A server copy that drifts from the client copy means a
 * future tenant rename surfaces legacy docs on one side and hides them on the
 * other — the kind of split-brain that only shows up as "empty in prod".
 *
 * `src/lib/tenant.ts` re-exports these and adds the browser-only
 * `getTenantIdSync()` (localStorage). Server code imports from here directly.
 */

/** Default tenant for legacy data or unauthenticated users. */
export const DEFAULT_TENANT_ID = 'techery-au';

/**
 * Map of tenantId → legacy alias values that historical documents may still
 * carry. Used by `tenantIdReadUnion()` so reads surface pre-migration data
 * until a backfill sweeps it.
 *
 * STATUS (2026-05-11): the legacy `'techery'` alias is DEAD — the burn-down
 * audited all top-level collections and migrated the last alias docs;
 * `legacy: 0` across the database. The map stays (empty) as policy insurance:
 * a future tenant rename drops a one-liner here and every union read surfaces
 * the old docs while a fresh backfill cleans up.
 */
const TENANT_ID_LEGACY_ALIASES: Record<string, string[]> = {
  // 'techery-au': ['techery'] — DROPPED 2026-05-11 after the burn-down
  // confirmed zero legacy docs remain.
};

/**
 * The list of tenantId values to include in a Firestore `in` query for the
 * given tenant: canonical first, then any legacy aliases.
 *
 *   where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync()))
 */
export function tenantIdReadUnion(tenantId: string): string[] {
  const aliases = TENANT_ID_LEGACY_ALIASES[tenantId] || [];
  return Array.from(new Set([tenantId, ...aliases]));
}

/**
 * Does a document belong to this tenant? For IN-MEMORY filtering of a query
 * that could not carry a `where('tenantId', ...)` clause.
 *
 * A doc with NO tenantId counts as belonging — the same deliberate rule
 * `customer.service.ts` uses on the client (`!c.tenantId || c.tenantId ===
 * current`). Pre-tenancy documents exist and a strict check would silently
 * drop them, which is worse than the (single-tenant-today) leak it prevents.
 * That tolerance is what makes this safe to apply to server queries that a
 * `where` clause would break.
 *
 * Prefer a `where('tenantId', 'in', tenantIdReadUnion(...))` clause when the
 * query can carry one — it's enforced by the database rather than by us.
 */
export function docBelongsToTenant(
  docTenantId: string | null | undefined,
  tenantId: string,
): boolean {
  if (docTenantId == null || docTenantId === '') return true;
  return tenantIdReadUnion(tenantId).includes(docTenantId);
}
